/**
 * Runs `fn` over `items` with at most `limit` promises in flight at once,
 * preserving input order in the returned array (drop-in for `Promise.all`).
 *
 * Why this exists: Supabase Storage operations (createSignedUrl, upload, …)
 * each cause the Storage API to take a Postgres connection from its pool.
 * Firing them with an uncapped `Promise.all` over a whole batch opens dozens
 * of Storage connections at once, which is what pushes the instance toward
 * its `max_connections` ceiling. Bounding the fan-out keeps that pool small.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  };

  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}
